import React, { createContext, useContext, useEffect, useState } from 'react';
import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import type { User } from '../types';

interface UserContextValue {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const UserContext = createContext<UserContextValue>({
  user: null,
  loading: true,
  refresh: async () => {},
});

export function useCurrentUser(): UserContextValue {
  return useContext(UserContext);
}

export function isOnyxFC(user: User | null): boolean {
  return !!user?.roles.includes('Onyx Financial Coordinator');
}

export function isBHSD(user: User | null): boolean {
  if (!user) return false;
  return (
    user.roles.includes('BHSD Super User') ||
    user.roles.includes('BHSD Program Manager')
  );
}

export function isBHSDProgramManager(user: User | null): boolean {
  return !!user?.roles.includes('BHSD Program Manager');
}

export function canSecondApprove(user: User | null): boolean {
  if (!user) return false;
  return (
    user.roles.includes('BHSD Super User') ||
    user.roles.includes('Onyx Financial Coordinator')
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export function UserContextProvider({ children }: { children: React.ReactNode }) {
  const { accounts, instance } = useMsal();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function fetchUser() {
    const account = accounts[0];
    if (!account) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const tokenResult = await instance.acquireTokenSilent({
        account,
        scopes: ['openid', 'profile'],
      });
      const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
      const res = await fetch(`${baseUrl}/api/users/me`, {
        headers: { Authorization: `Bearer ${tokenResult.idToken}` },
      });
      if (!res.ok) throw new Error('Failed to load user profile');
      const data = (await res.json()) as User;
      setUser(data);
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError || err instanceof BrowserAuthError) {
        await instance.loginRedirect({ scopes: ['openid', 'profile'] });
        return;
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.length]);

  return (
    <UserContext.Provider value={{ user, loading, refresh: fetchUser }}>
      {children}
    </UserContext.Provider>
  );
}
